import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Switch } from "../";
import ExtraOptions from "./ExtraOptions";

const ExtrasList = ({
  businessId,
  hasPosIntegration,
  extras,
  isLoadingExtras,
  selectedProduct,
  onToggleExtra,
  isLoadingToggle,
  onCreateExtra,
  onEditExtra,
  onDeleteExtra,
  isDeletingExtra,
}) => {
  const { t } = useTranslation("products");
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState({});
  const [isActiveExtrasExpanded, setIsActiveExtrasExpanded] = useState(true);
  const [isAvailableExtrasExpanded, setIsAvailableExtrasExpanded] =
    useState(false);

  if (!selectedProduct) return null;

  const toggleExpanded = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const productExtras = selectedProduct.extras ?? [];
  const productExtraIds = new Set(
    productExtras.map((e) => e?.id).filter(Boolean),
  );

  const availableExtras = extras.filter(
    (extra) => !productExtraIds.has(extra.id),
  );

  // `selectedProduct.extras` can contain null/undefined entries; filter defensively before sorting.
  const sortedProductExtras = productExtras
    .filter(Boolean)
    .sort((a, b) => (a.rank || 0) - (b.rank || 0));
  const sortedAvailableExtras = [...availableExtras].sort(
    (a, b) => (a.rank || 0) - (b.rank || 0),
  );

  const lowerSearch = searchTerm.toLowerCase();
  const filteredProductExtras = searchTerm
    ? sortedProductExtras.filter((e) =>
        e.name?.toLowerCase().includes(lowerSearch),
      )
    : sortedProductExtras;
  const filteredAvailableExtras = searchTerm
    ? sortedAvailableExtras.filter((e) =>
        e.name?.toLowerCase().includes(lowerSearch),
      )
    : sortedAvailableExtras;

  const hasProductExtras = filteredProductExtras.length > 0;
  const hasAvailableExtras = filteredAvailableExtras.length > 0;
  const hasAnyExtras =
    hasProductExtras ||
    hasAvailableExtras ||
    extras.length > 0 ||
    sortedProductExtras.length > 0;

  const renderExtraItem = (extra, isEnabled) => {
    return (
      <div
        key={extra.id}
        className="rounded-lg border border-gray-700 bg-gray-800"
      >
        <div className="flex items-center justify-between p-3">
          <button
            onClick={() => toggleExpanded(extra.id)}
            className="hover:bg-gray-750/20 flex min-w-0 flex-1 items-center gap-2 rounded text-left"
            aria-label="Toggle options"
          >
            <Icon
              name={expanded[extra.id] ? "ChevronDown" : "ChevronRight"}
              className="h-4 w-4 flex-shrink-0 text-gray-400"
            />
            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-medium text-white">
                {extra.name}
              </h4>
              {extra.description && (
                <p className="mt-1 truncate text-xs text-gray-400">
                  {extra.description}
                </p>
              )}
            </div>
          </button>
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {!hasPosIntegration && (
              <>
                <button
                  onClick={() => onEditExtra(extra)}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-700 hover:text-blue-400"
                  aria-label="Edit extra"
                  title="Edit extra"
                >
                  <Icon name="Edit" className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDeleteExtra(extra)}
                  disabled={isDeletingExtra}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-700 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Delete extra"
                  title="Delete extra"
                >
                  <Icon name="Trash" className="h-4 w-4" />
                </button>
              </>
            )}
            <Switch
              checked={!!isEnabled}
              onCheckedChange={() =>
                onToggleExtra(extra, selectedProduct.extras)
              }
              disabled={isLoadingToggle}
            />
          </div>
        </div>

        {expanded[extra.id] && (
          <div className="border-t border-gray-700">
            <ExtraOptions
              businessId={businessId}
              extraId={extra.id}
              hasPosIntegration={hasPosIntegration}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-300">Extras</div>
        {!hasPosIntegration && (
          <div className="sm:ml-auto">
            <button
              className="flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onCreateExtra}
            >
              <Icon name="Plus" className="h-4 w-4" /> Add Extra
            </button>
          </div>
        )}
      </div>

      {/* Search Input */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon name="Search" className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder={t("business.extras.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pr-10 pl-10 text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
          >
            <Icon name="X" className="h-4 w-4 text-gray-400 hover:text-white" />
          </button>
        )}
      </div>

      {isLoadingExtras ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
        </div>
      ) : hasAnyExtras ? (
        <>
          {/* Active Extras Section */}
          {hasProductExtras && (
            <div className="space-y-3">
              <button
                onClick={() =>
                  setIsActiveExtrasExpanded(!isActiveExtrasExpanded)
                }
                className="group flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-gray-800/50"
                aria-label={
                  isActiveExtrasExpanded
                    ? "Collapse active extras"
                    : "Expand active extras"
                }
              >
                <div className="flex items-center gap-2">
                  <Icon
                    name={
                      isActiveExtrasExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    className="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-hover:text-gray-300"
                  />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Active Extras
                  </h3>
                  <span className="rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-400">
                    {filteredProductExtras.length}
                  </span>
                </div>
              </button>
              {isActiveExtrasExpanded && (
                <div className="space-y-2 pl-7">
                  {filteredProductExtras.map((extra) =>
                    renderExtraItem(extra, true),
                  )}
                </div>
              )}
            </div>
          )}

          {/* Available Extras Section */}
          {hasAvailableExtras && (
            <div className="space-y-3">
              {hasProductExtras && (
                <div className="border-t border-gray-800 pt-4"></div>
              )}
              <button
                onClick={() =>
                  setIsAvailableExtrasExpanded(!isAvailableExtrasExpanded)
                }
                className="group flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-gray-800/50"
                aria-label={
                  isAvailableExtrasExpanded
                    ? "Collapse available extras"
                    : "Expand available extras"
                }
              >
                <div className="flex items-center gap-2">
                  <Icon
                    name={
                      isAvailableExtrasExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    className="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-hover:text-gray-300"
                  />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Available Extras
                  </h3>
                  <span className="rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-400">
                    {filteredAvailableExtras.length}
                  </span>
                </div>
              </button>
              {isAvailableExtrasExpanded && (
                <div className="space-y-2 pl-7">
                  {filteredAvailableExtras.map((extra) =>
                    renderExtraItem(extra, false),
                  )}
                </div>
              )}
            </div>
          )}

          {/* No search results */}
          {searchTerm && !hasProductExtras && !hasAvailableExtras && (
            <div className="py-8 text-center">
              <Icon
                name="Search"
                className="mx-auto mb-2 h-12 w-12 text-gray-400"
              />
              <p className="text-sm text-gray-400">
                {t("business.extras.noSearchResults")}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="py-8 text-center">
          <Icon name="Tag" className="mx-auto mb-2 h-12 w-12 text-gray-400" />
          <p className="text-sm text-gray-400">No extras available</p>
        </div>
      )}
    </div>
  );
};

export default ExtrasList;
